
import { useEffect, useState } from 'react';

interface LoadingScreenProps {
  onLoadingComplete: () => void;
}

export default function LoadingScreen({ onLoadingComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [showLogo, setShowLogo] = useState(false);
  const [showIrrigation, setShowIrrigation] = useState(false);

  useEffect(() => {
    // Logo animasyonunu başlat
    const logoTimer = setTimeout(() => {
      setShowLogo(true);
    }, 300);

    // Sulama animasyonunu başlat
    const irrigationTimer = setTimeout(() => {
      setShowIrrigation(true);
    }, 800);

    // Progress bar animasyonu
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            onLoadingComplete();
          }, 1000);
          return 100;
        }
        return prev + 1.5;
      });
    }, 40);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(irrigationTimer);
      clearInterval(progressInterval);
    };
  }, [onLoadingComplete]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-green-50 via-white to-green-100 flex items-center justify-center z-50 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Floating Water Drops */}
        <div className="absolute top-20 left-20 w-4 h-4 bg-blue-400 rounded-full opacity-30 animate-float"></div>
        <div className="absolute top-32 right-32 w-3 h-3 bg-blue-500 rounded-full opacity-40 animate-float delay-500"></div>
        <div className="absolute bottom-40 left-40 w-5 h-5 bg-blue-300 rounded-full opacity-25 animate-float delay-1000"></div>
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-blue-600 rounded-full opacity-35 animate-float delay-700"></div>
        
        {/* Background Gradient Circles */}
        <div className="absolute top-10 left-10 w-40 h-40 bg-green-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-green-300 rounded-full blur-3xl opacity-15 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-blue-200 rounded-full blur-3xl opacity-10 animate-pulse delay-500"></div>
      </div>

      <div className="text-center relative z-10 max-w-2xl mx-auto px-8">
        {/* Logo Container */}
        <div className={`mb-16 transition-all duration-1500 ease-out ${showLogo ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 translate-y-8'}`}>
          {/* Main Logo */}
          <div className="flex items-center justify-center mb-8">
            <div className="relative">
              {/* Main Logo without Circle */}
              <div className="relative">
                <img 
                  src="https://static.readdy.ai/image/6464ee4f71a2ee332e3d69d3173c1c98/1821839d44cea169d9473da199a17a94.png" 
                  alt="GüvenTar Logo" 
                  className="w-48 h-48 object-contain"
                />
              </div>
              
              {/* Ripple Effects */}
              <div className="absolute inset-0 w-48 h-48 bg-green-400 rounded-full animate-ping opacity-20"></div>
              <div className="absolute -inset-2 w-52 h-52 bg-green-500 rounded-full animate-ping opacity-15 delay-300"></div>
              <div className="absolute -inset-4 w-56 h-56 bg-green-600 rounded-full animate-ping opacity-10 delay-600"></div>
            </div>
          </div>

          {/* Animated Tagline */}
          <div className="text-gray-600 text-xl font-medium mb-12 animate-fade-in-up delay-500">
            <span className="inline-block">Toprağa Güven,</span>
            <span className="inline-block text-green-800 ml-2 font-semibold">Suya Değer Kat</span>
          </div>
        </div>

        {/* Advanced Irrigation Animation */}
        <div className={`mb-16 transition-all duration-1000 ease-out ${showIrrigation ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Main Irrigation System */}
          <div className="relative flex items-center justify-center">
            {/* Main Pipe System */}
            <div className="relative">
              {/* Horizontal Main Pipe */}
              <div className="w-80 h-4 bg-gradient-to-r from-gray-400 via-gray-500 to-gray-400 rounded-full relative overflow-hidden shadow-lg">
                {/* Water Flow Animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full animate-pulse opacity-80"></div>
                <div className="absolute top-1 left-1 right-1 bottom-1 bg-gradient-to-r from-blue-300 to-blue-400 rounded-full animate-flow"></div>
              </div>
              
              {/* Vertical Distribution Pipes */}
              <div className="absolute top-4 left-16 w-2 h-12 bg-gray-500 rounded-full"></div>
              <div className="absolute top-4 left-32 w-2 h-12 bg-gray-500 rounded-full"></div>
              <div className="absolute top-4 left-48 w-2 h-12 bg-gray-500 rounded-full"></div>
              <div className="absolute top-4 left-64 w-2 h-12 bg-gray-500 rounded-full"></div>
              
              {/* Water Droplets */}
              <div className="absolute top-16 left-16">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-drip opacity-80"></div>
              </div>
              <div className="absolute top-16 left-32">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-drip delay-200 opacity-80"></div>
              </div>
              <div className="absolute top-16 left-48">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-drip delay-400 opacity-80"></div>
              </div>
              <div className="absolute top-16 left-64">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-drip delay-600 opacity-80"></div>
              </div>
              
              {/* Additional Falling Drops */}
              <div className="absolute top-20 left-20">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-fall opacity-60"></div>
              </div>
              <div className="absolute top-20 left-36">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-fall delay-300 opacity-60"></div>
              </div>
              <div className="absolute top-20 left-52">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-fall delay-600 opacity-60"></div>
              </div>
              <div className="absolute top-20 left-68">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-fall delay-900 opacity-60"></div>
              </div>
            </div>
          </div>

          {/* Growing Plants */}
          <div className="flex justify-center space-x-8 mt-12">
            <div className="flex flex-col items-center">
              <div className="relative">
                <i className="ri-plant-line text-green-500 text-3xl animate-grow-plant"></i>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-1 h-8 bg-green-400 animate-grow-stem"></div>
              </div>
              <div className="w-8 h-2 bg-amber-600 rounded-full mt-2"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="relative">
                <i className="ri-plant-line text-green-600 text-4xl animate-grow-plant delay-300"></i>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-1 h-10 bg-green-500 animate-grow-stem delay-300"></div>
              </div>
              <div className="w-8 h-2 bg-amber-600 rounded-full mt-2"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="relative">
                <i className="ri-plant-line text-green-500 text-3xl animate-grow-plant delay-600"></i>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-1 h-8 bg-green-400 animate-grow-stem delay-600"></div>
              </div>
              <div className="w-8 h-2 bg-amber-600 rounded-full mt-2"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="relative">
                <i className="ri-plant-line text-green-600 text-4xl animate-grow-plant delay-900"></i>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-1 h-10 bg-green-500 animate-grow-stem delay-900"></div>
              </div>
              <div className="w-8 h-2 bg-amber-600 rounded-full mt-2"></div>
            </div>
          </div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="w-96 mx-auto">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-gray-600 font-medium">Sistem Hazırlanıyor...</span>
            <span className="text-sm text-green-700 font-bold">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
            <div 
              className="h-full bg-gradient-to-r from-green-500 via-green-600 to-green-700 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
              style={{ width: `${progress}%` }}
            >
              {/* Progress Bar Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine"></div>
            </div>
          </div>
        </div>

        {/* Loading Text with Animation */}
        <div className="mt-10 text-gray-600 text-base">
          <div className="flex items-center justify-center space-x-2">
            <span className="font-medium">Sulama sistemi başlatılıyor</span>
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce delay-100"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce delay-200"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Corner Decorative Elements */}
      <div className="absolute top-8 left-8 opacity-20">
        <i className="ri-drop-line text-blue-500 text-2xl animate-float"></i>
      </div>
      <div className="absolute top-8 right-8 opacity-20">
        <i className="ri-plant-line text-green-500 text-2xl animate-float delay-500"></i>
      </div>
      <div className="absolute bottom-8 left-8 opacity-20">
        <i className="ri-water-flash-line text-blue-400 text-xl animate-float delay-1000"></i>
      </div>
      <div className="absolute bottom-8 right-8 opacity-20">
        <i className="ri-leaf-line text-green-400 text-xl animate-float delay-700"></i>
      </div>
    </div>
  );
}
